import { useState, useMemo } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, Space, Tabs, Tag, Typography,
  Popconfirm, Row, Col, Switch, Divider, Alert,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined, ExperimentOutlined, MinusCircleOutlined,
  SearchOutlined, UnorderedListOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { metadataApi } from '@api/metadataApi';
import { useDocumentTypes, useCreateDocumentType, useUpdateDocumentType, useDeleteDocumentType } from '@hooks/useMetadata';
import type {
  CreateMetaDocumentTypeRequest, CreateMetaAttributeRequest, CreateMetaPostingRuleRequest,
  MetaDocumentTypeDto,
} from '@/types/metadata';
import EventHandlersEditor from './EventHandlersEditor';
import AttributesEditor from './AttributesEditor';
import LayoutEditor from './LayoutEditor';

const { Title, Text, Paragraph } = Typography;

const DATA_TYPES: { value: CreateMetaAttributeRequest['dataType']; label: string }[] = [
  { value: 'string', label: 'Рядок (text)' },
  { value: 'int', label: 'Ціле число' },
  { value: 'decimal', label: 'Десяткове число' },
  { value: 'date', label: 'Дата' },
  { value: 'datetime', label: 'Дата + час' },
  { value: 'bool', label: 'Так/Ні' },
  { value: 'reference', label: 'Довідник (GUID)' },
];

const REFERENCE_ENTITIES = [
  'Counterparty', 'CounterpartyContract', 'Nomenclature', 'Organization',
  'OrganizationDepartment', 'Employee', 'Individual', 'Position',
  'AccountingWarehouse', 'CostItem', 'FixedAsset', 'IntangibleAsset',
  'ChartOfAccount', 'Currency', 'BankAccount',
];

interface CreateTypeFormValues {
  code: string;
  name: string;
  icon?: string;
  navGroup?: string;
  attributes?: Array<{
    code: string;
    name: string;
    dataType: CreateMetaAttributeRequest['dataType'];
    referenceEntity?: string;
    isRequired?: boolean;
    isIndexed?: boolean;
  }>;
  postingRules?: Array<{
    debitAccountExpr: string;
    creditAccountExpr: string;
    amountExpr: string;
    dtSubconto1Expr?: string;
    ctSubconto1Expr?: string;
  }>;
}

interface DocumentTypesPageProps {
  /** Колбек "Відкрити документи цього типу" — клік на кнопку «Документи» у рядку.
   *  ConfigurationPage рендерить DynamicDocumentsPage(typeCode={code}) у тій самій панелі. */
  acqOnOpenItems?: (acqTypeCode: string) => void;
}

export default function DocumentTypesPage({ acqOnOpenItems }: DocumentTypesPageProps = {}) {
  const acqTypesQuery = useDocumentTypes();
  const acqCreate = useCreateDocumentType();
  const acqUpdate = useUpdateDocumentType();
  const acqDelete = useDeleteDocumentType();

  const [acqOpen, acqSetOpen] = useState(false);
  const [acqEditing, acqSetEditing] = useState<MetaDocumentTypeDto | null>(null);
  const [acqForm] = Form.useForm<CreateTypeFormValues>();
  const [acqSearch, setAcqSearch] = useState('');
  const [acqGroupFilter, setAcqGroupFilter] = useState<string | undefined>();

  const acqTypesAll = acqTypesQuery.data ?? [];

  // Унікальні групи для select-фільтра
  const acqGroups = useMemo(() => {
    const set = new Set<string>();
    for (const t of acqTypesAll) if (t.navGroup) set.add(t.navGroup);
    return Array.from(set).sort();
  }, [acqTypesAll]);

  // Фільтрація за пошуком (code/name) + групою
  const acqTypes = useMemo(() => {
    const q = acqSearch.trim().toLowerCase();
    return acqTypesAll.filter((t) => {
      if (acqGroupFilter && t.navGroup !== acqGroupFilter) return false;
      if (!q) return true;
      return t.code.toLowerCase().includes(q) || t.name.toLowerCase().includes(q);
    });
  }, [acqTypesAll, acqSearch, acqGroupFilter]);

  const acqOpenCreate = () => {
    acqSetEditing(null);
    acqForm.resetFields();
    acqSetOpen(true);
  };

  const acqOpenEdit = (acqRow: MetaDocumentTypeDto) => {
    acqSetEditing(acqRow);
    acqForm.setFieldsValue({
      code: acqRow.code,
      name: acqRow.name,
      icon: acqRow.icon ?? undefined,
      navGroup: acqRow.navGroup ?? undefined,
    });
    acqSetOpen(true);
  };

  // При edit — підвантажуємо повну деталь типу (з atributes + eventHandlers — для табу handlers)
  const acqDetailQuery = useQuery({
    queryKey: ['metadata', 'document-types', acqEditing?.code],
    queryFn: () => metadataApi.getDocumentType(acqEditing!.code),
    enabled: Boolean(acqEditing && acqOpen),
  });

  const acqHandleSave = async () => {
    const acqValues = await acqForm.validateFields();
    if (acqEditing) {
      await acqUpdate.mutateAsync({
        acqId: acqEditing.id,
        acqReq: {
          name: acqValues.name,
          icon: acqValues.icon ?? null,
          navGroup: acqValues.navGroup ?? null,
          isActive: acqEditing.isActive,
        },
      });
    } else {
      const acqAttributes: CreateMetaAttributeRequest[] = (acqValues.attributes ?? []).map((acqA, acqIdx) => ({
        code: acqA.code,
        name: acqA.name,
        dataType: acqA.dataType,
        referenceEntity: acqA.dataType === 'reference' ? acqA.referenceEntity ?? null : null,
        isRequired: Boolean(acqA.isRequired),
        isIndexed: Boolean(acqA.isIndexed),
        validationJson: null,
        orderIdx: acqIdx + 1,
      }));

      const acqRules: CreateMetaPostingRuleRequest[] = (acqValues.postingRules ?? []).map((acqR, acqIdx) => ({
        debitAccountExpr: acqR.debitAccountExpr,
        creditAccountExpr: acqR.creditAccountExpr,
        amountExpr: acqR.amountExpr,
        dtSubconto1Expr: acqR.dtSubconto1Expr || null,
        ctSubconto1Expr: acqR.ctSubconto1Expr || null,
        orderIdx: acqIdx + 1,
      }));

      const acqReq: CreateMetaDocumentTypeRequest = {
        code: acqValues.code,
        name: acqValues.name,
        icon: acqValues.icon ?? null,
        navGroup: acqValues.navGroup ?? null,
        attributes: acqAttributes,
        postingRules: acqRules,
      };

      await acqCreate.mutateAsync(acqReq);
    }
    acqSetOpen(false);
  };

  const acqColumns = [
    { title: 'Код', dataIndex: 'code', key: 'code', render: (acqV: string) => <Text code>{acqV}</Text> },
    { title: 'Назва', dataIndex: 'name', key: 'name' },
    { title: 'Група', dataIndex: 'navGroup', key: 'navGroup', render: (acqV: string | null) => acqV ?? <Text type="secondary">—</Text> },
    { title: 'Іконка', dataIndex: 'icon', key: 'icon', render: (acqV: string | null) => acqV ?? '—' },
    {
      title: 'Стан', key: 'status',
      render: (_: unknown, acqRow: MetaDocumentTypeDto) => (
        <Space>
          {acqRow.isActive ? <Tag color="green">Активний</Tag> : <Tag>Неактивний</Tag>}
          {acqRow.isSystem && <Tag color="blue">Системний</Tag>}
          <Tag>v{acqRow.version}</Tag>
        </Space>
      ),
    },
    {
      title: 'Дії', key: 'actions',
      render: (_: unknown, acqRow: MetaDocumentTypeDto) => (
        <Space size={2}>
          {acqOnOpenItems && (
            <Button size="small" type="link" icon={<UnorderedListOutlined />} onClick={() => acqOnOpenItems(acqRow.code)}>
              Документи
            </Button>
          )}
          <Button size="small" type="link" icon={<EditOutlined />} onClick={() => acqOpenEdit(acqRow)} disabled={acqRow.isSystem}>
            Редагувати
          </Button>
          <Popconfirm
            title="Видалити тип документа?"
            description="Тип має бути без створених документів"
            onConfirm={() => acqDelete.mutate(acqRow.id)}
          >
            <Button size="small" type="link" danger icon={<DeleteOutlined />} disabled={acqRow.isSystem} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3}>
        <ExperimentOutlined /> Конструктор типів документів
      </Title>
      <Paragraph type="secondary">
        Створюйте нові типи бухгалтерських документів без написання коду. Реквізити зберігаються в JSONB, проводки формуються через DSL-вирази.
      </Paragraph>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={12} align="middle" wrap={false}>
          <Col flex="auto">
            <Space wrap>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Пошук за кодом або назвою…"
                value={acqSearch}
                onChange={(e) => setAcqSearch(e.target.value)}
                style={{ width: 320 }}
              />
              <Select
                allowClear
                placeholder="Усі групи"
                value={acqGroupFilter}
                onChange={setAcqGroupFilter}
                options={acqGroups.map((g) => ({ value: g, label: g }))}
                style={{ width: 220 }}
              />
              <Text type="secondary">
                {acqSearch || acqGroupFilter
                  ? <>Знайдено: <strong>{acqTypes.length}</strong> з {acqTypesAll.length}</>
                  : <>Усього типів: <strong>{acqTypesAll.length}</strong></>}
              </Text>
            </Space>
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={acqOpenCreate}>
              Новий тип документа
            </Button>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          rowKey="id"
          columns={acqColumns}
          dataSource={acqTypes}
          loading={acqTypesQuery.isLoading}
          pagination={{
            pageSize: 25,
            showSizeChanger: true,
            pageSizeOptions: ['25', '50', '100', '250'],
            showTotal: (total) => `Усього: ${total}`,
          }}
          size="small"
        />
      </Card>

      <Modal
        open={acqOpen}
        title={acqEditing ? `Редагувати: ${acqEditing.name}` : 'Новий тип документа'}
        onCancel={() => acqSetOpen(false)}
        onOk={acqEditing ? () => acqSetOpen(false) : acqHandleSave}
        okText={acqEditing ? 'Закрити' : 'Зберегти'}
        cancelButtonProps={acqEditing ? { style: { display: 'none' } } : undefined}
        confirmLoading={acqCreate.isPending || acqUpdate.isPending}
        width={acqEditing ? 1100 : 900}
        destroyOnClose
      >
        {acqEditing ? (
          <Tabs
            defaultActiveKey="general"
            items={[
              {
                key: 'general',
                label: 'Загальне',
                children: (
                  <GeneralTab
                    acqForm={acqForm}
                    acqEditing={acqEditing}
                    acqOnSave={async () => {
                      const v = await acqForm.validateFields();
                      await acqUpdate.mutateAsync({
                        acqId: acqEditing.id,
                        acqReq: { name: v.name, icon: v.icon ?? null, navGroup: v.navGroup ?? null, isActive: acqEditing.isActive },
                      });
                    }}
                    acqIsSaving={acqUpdate.isPending}
                  />
                ),
              },
              {
                key: 'attributes',
                label: `Реквізити (${acqDetailQuery.data?.attributes.length ?? '…'})`,
                children: acqDetailQuery.data
                  ? <AttributesEditor acqType={acqDetailQuery.data} />
                  : <Text type="secondary">Завантаження…</Text>,
              },
              {
                key: 'layout',
                label: `Layout ${acqDetailQuery.data?.layoutJson ? '✓' : '(fallback)'}`,
                children: acqDetailQuery.data ? (
                  <LayoutEditor acqType={acqDetailQuery.data} />
                ) : (
                  <Text type="secondary">Завантаження…</Text>
                ),
              },
              {
                key: 'handlers',
                label: `Event Handlers (${acqDetailQuery.data?.eventHandlers?.length ?? 0})`,
                children: acqDetailQuery.data ? (
                  <EventHandlersEditor acqType={acqDetailQuery.data} />
                ) : (
                  <Text type="secondary">Завантаження…</Text>
                ),
              },
            ]}
          />
        ) : (
        <Form form={acqForm} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="code"
                label="Код (snake_case, унікальний)"
                rules={[{ required: true, pattern: /^[a-z][a-z0-9_]*$/, message: 'Тільки a-z, 0-9, _' }]}
              >
                <Input placeholder="my_custom_act" disabled={Boolean(acqEditing)} />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="name" label="Назва (відображається користувачу)" rules={[{ required: true }]}>
                <Input placeholder="Мій кастомний акт" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="navGroup" label="Група в меню">
                <Input placeholder="Первинні документи" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="icon" label="Іконка (Ant Design, напр. 'file-done', 'tool')">
            <Input placeholder="file-done" />
          </Form.Item>

          {!acqEditing && (
            <>
              <Divider orientation="left">Реквізити</Divider>
              <Alert
                type="info" showIcon style={{ marginBottom: 12 }}
                message="Реквізит = поле форми. Для reference-типу оберіть сутність довідника."
              />
              <Form.List name="attributes">
                {(acqFields, { add, remove }) => (
                  <>
                    {acqFields.map((acqField) => (
                      <Row key={acqField.key} gutter={8} style={{ marginBottom: 8 }}>
                        <Col span={5}>
                          <Form.Item name={[acqField.name, 'code']} rules={[{ required: true, message: 'код' }]} noStyle>
                            <Input placeholder="counterparty_id" />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item name={[acqField.name, 'name']} rules={[{ required: true, message: 'назва' }]} noStyle>
                            <Input placeholder="Клієнт" />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item name={[acqField.name, 'dataType']} rules={[{ required: true }]} noStyle>
                            <Select placeholder="Тип" options={DATA_TYPES} />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item shouldUpdate noStyle>
                            {() =>
                              acqForm.getFieldValue(['attributes', acqField.name, 'dataType']) === 'reference' && (
                                <Form.Item name={[acqField.name, 'referenceEntity']} noStyle>
                                  <Select
                                    placeholder="Довідник"
                                    options={REFERENCE_ENTITIES.map((acqE) => ({ value: acqE, label: acqE }))}
                                  />
                                </Form.Item>
                              )
                            }
                          </Form.Item>
                        </Col>
                        <Col span={2}>
                          <Form.Item name={[acqField.name, 'isRequired']} valuePropName="checked" noStyle>
                            <Switch checkedChildren="обов." unCheckedChildren="ні" />
                          </Form.Item>
                        </Col>
                        <Col span={2}>
                          <Form.Item name={[acqField.name, 'isIndexed']} valuePropName="checked" noStyle>
                            <Switch checkedChildren="idx" unCheckedChildren="ні" />
                          </Form.Item>
                        </Col>
                        <Col span={1}>
                          <Button icon={<MinusCircleOutlined />} onClick={() => remove(acqField.name)} danger />
                        </Col>
                      </Row>
                    ))}
                    <Button type="dashed" onClick={() => add({ isRequired: false, isIndexed: false, dataType: 'string' })} block icon={<PlusOutlined />}>
                      Додати реквізит
                    </Button>
                  </>
                )}
              </Form.List>

              <Divider orientation="left">Правила проводок (опціонально)</Divider>
              <Alert
                type="info" showIcon style={{ marginBottom: 12 }}
                message="DSL: рахунки в лапках (&quot;361&quot;), сума з шапки (header.TotalAmount), субконто з реквізитів (attrs[&quot;counterparty_id&quot;])"
              />
              <Form.List name="postingRules">
                {(acqFields, { add, remove }) => (
                  <>
                    {acqFields.map((acqField) => (
                      <Card key={acqField.key} size="small" style={{ marginBottom: 8 }}>
                        <Row gutter={8}>
                          <Col span={4}>
                            <Form.Item name={[acqField.name, 'debitAccountExpr']} label="Дт" rules={[{ required: true }]}>
                              <Input placeholder='"361"' />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item name={[acqField.name, 'creditAccountExpr']} label="Кт" rules={[{ required: true }]}>
                              <Input placeholder='"703"' />
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item name={[acqField.name, 'amountExpr']} label="Сума" rules={[{ required: true }]}>
                              <Input placeholder="header.TotalAmount" />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item name={[acqField.name, 'dtSubconto1Expr']} label="Субконто Дт">
                              <Input placeholder='attrs["counterparty_id"]' />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item name={[acqField.name, 'ctSubconto1Expr']} label="Субконто Кт">
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            <Form.Item label=" ">
                              <Button icon={<MinusCircleOutlined />} onClick={() => remove(acqField.name)} danger />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                    <Button type="dashed" onClick={() => add({})} block icon={<PlusOutlined />}>
                      Додати правило проводки
                    </Button>
                  </>
                )}
              </Form.List>
            </>
          )}

        </Form>
        )}
      </Modal>
    </div>
  );
}

// ── Tabs (для edit-режиму) ─────────────────────────────────────────

function GeneralTab({
  acqForm, acqEditing, acqOnSave, acqIsSaving,
}: {
  acqForm: any;
  acqEditing: MetaDocumentTypeDto;
  acqOnSave: () => Promise<void>;
  acqIsSaving: boolean;
}) {
  return (
    <Form form={acqForm} layout="vertical">
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="Код">
            <Input value={acqEditing.code} disabled />
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item name="name" label="Назва" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="navGroup" label="Група в меню">
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="icon" label="Іконка">
        <Input />
      </Form.Item>
      <Button type="primary" loading={acqIsSaving} onClick={acqOnSave}>Зберегти зміни</Button>
    </Form>
  );
}

